import React, { useCallback, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Tooltip,
  Avatar,
} from '@mui/material';

import { useRootStore } from 'stores/RootStore';
import { SharedWithUser, UnderwritingProperty } from '../types';
import { ShareAccessModal } from '../modals/ShareAccessModal';
import { UserOutput } from 'dtos/users/UserOutput';

const renderInitials = (name: string = ''): string => name.replace(/[^A-Z]/g, '').substring(0, 2);

const renderCollapsedUsers = (collapsedUsers: SharedWithUser[]) => (
  <div>
    {collapsedUsers.map(({ name }, index) => (
      <div key={index}> {name} </div>
    ))}
  </div>
);

const shouldDisableShareAccess = (property: UnderwritingProperty, user?: UserOutput) =>
  !user ||
  !!(property.sharedWith || []).some(({ email, accessLevel }) => email === user.email && accessLevel === 'view');

const DISPLAYED_APPRAISERS_NUMBER = 5;

const SharedWithList = ({ sharedWith }: { sharedWith: SharedWithUser[] }) => {
  const displayedUsers =
    sharedWith.length > DISPLAYED_APPRAISERS_NUMBER ? sharedWith.slice(0, DISPLAYED_APPRAISERS_NUMBER - 1) : sharedWith;
  const collapsedUsers = sharedWith.slice(displayedUsers.length);

  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginLeft: 'auto' }}>
      {displayedUsers.map((appraiser, index) => (
        <Tooltip key={index} title={appraiser.name} placement="top-end">
          <Avatar src={appraiser.photo} alt={appraiser.name}>
            {renderInitials(appraiser.name)}
          </Avatar>
        </Tooltip>
      ))}
      {collapsedUsers.length > 0 && (
        <Tooltip key="collapsed-users" placement="top-end" title={renderCollapsedUsers(collapsedUsers)}>
          <Avatar>{`+${collapsedUsers.length}`}</Avatar>
        </Tooltip>
      )}
    </div>
  );
};

type UnderwritingDashboardProps = {
  properties: UnderwritingProperty[];
};

const UnderwritingDashboard: React.FC<UnderwritingDashboardProps> = observer(({ properties }) => {
  const { underwritingDashboardPageStore: pageStore, routeStore, authStore } = useRootStore();
  const { showShareModal } = pageStore;
  const { currentUser } = authStore;

  const [shareAccessModalState, setShareAccessModalState] = useState<{
    open: boolean;
    property: UnderwritingProperty | null;
  }>({
    open: false,
    property: null,
  });

  const handleShareAccessClick = useCallback(
    (property: UnderwritingProperty) => {
      if (!property?.id) {
        return;
      }

      showShareModal(property);
      setShareAccessModalState({ open: true, property });
    },
    [showShareModal]
  );

  const closeShareAccessModal = useCallback(() => {
    setShareAccessModalState({ open: false, property: null });
  }, []);

  const handleRowClick = useCallback(
    (item: UnderwritingProperty) => {
      routeStore.navigateByName('propertySummary', { id: item.id });
    },
    [routeStore]
  );

  return (
    <>
      <Paper elevation={2} sx={{ p: 2 }}>
        <TableContainer>
          <Table size="medium" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Address</TableCell>
                <TableCell>Property Type</TableCell>
                <TableCell align="right">Res. Units</TableCell>
                <TableCell align="right">Comm. Units</TableCell>
                <TableCell align="right"></TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {properties.map((item) => (
                <TableRow key={item.id} onClick={() => handleRowClick(item)} hover sx={{ cursor: 'pointer' }}>
                  <TableCell>{item.streetAddress}</TableCell>
                  <TableCell>{item.propertyType}</TableCell>
                  <TableCell align="right">{item.residentialUnits}</TableCell>
                  <TableCell align="right">{item.commercialUnits}</TableCell>
                  <TableCell width="250px" align="right">
                    <SharedWithList sharedWith={item.sharedWith || []} />
                  </TableCell>
                  <TableCell width="175px" align="right" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="outlined"
                      onClick={() => handleShareAccessClick(item)}
                      disabled={shouldDisableShareAccess(item, currentUser)}
                    >
                      Share Access
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {shareAccessModalState.open && shareAccessModalState.property && pageStore.shareAccessStore && (
        <ShareAccessModal
          store={pageStore.shareAccessStore}
          property={shareAccessModalState.property}
          onClose={closeShareAccessModal}
        />
      )}
    </>
  );
});

export { UnderwritingDashboard };
